import React, { useState } from 'react';
import { useInput, InputProps } from 'react-admin';
import { 
  FormControl, 
  FormLabel, 
  FormHelperText, 
  Box, 
  Button, 
  Popover, 
  TextField 
} from '@mui/material';
import { ChromePicker, ColorResult } from 'react-color';

interface ColorPickerInputProps extends Omit<InputProps, 'source'> {
  source: string;
  label?: string;
  helperText?: string;
  presetColors?: string[];
  disableAlpha?: boolean;
}

export const ColorPickerInput: React.FC<ColorPickerInputProps> = ({
  source,
  label,
  helperText,
  presetColors = ['#1C5195', '#4CAF50', '#FF9800', '#F44336', '#9C27B0', '#2196F3', '#757575'],
  disableAlpha = true,
  ...rest
}) => {
  const { field, fieldState } = useInput({ source, ...rest });
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [tempColor, setTempColor] = useState(field.value || '#1C5195');

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setTempColor(field.value || '#1C5195');
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleColorChange = (color: ColorResult) => {
    const colorValue = disableAlpha ? color.hex : `rgba(${color.rgb.r}, ${color.rgb.g}, ${color.rgb.b}, ${color.rgb.a})`;
    setTempColor(colorValue);
  };

  const handleApply = () => {
    field.onChange(tempColor);
    handleClose();
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    field.onChange(value);
  };

  const isOpen = Boolean(anchorEl);

  return (
    <FormControl fullWidth error={fieldState.invalid}>
      {label && <FormLabel>{label}</FormLabel>}
      
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
        <TextField
          value={field.value || ''}
          onChange={handleTextChange}
          placeholder="#1C5195"
          size="small"
          sx={{ flex: 1 }}
          inputProps={{
            pattern: '^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$',
          }}
        />
        
        <Button
          onClick={handleClick}
          sx={{
            minWidth: 48,
            height: 40,
            backgroundColor: field.value || '#1C5195',
            border: '2px solid #ddd',
            borderRadius: 1,
            '&:hover': {
              backgroundColor: field.value || '#1C5195',
              opacity: 0.8,
            },
          }}
        >
          <Box
            sx={{
              width: 24,
              height: 24,
              backgroundColor: field.value || '#1C5195',
              borderRadius: '50%',
            }}
          />
        </Button>
      </Box>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: { mt: 1 }
        }}
      >
        <Box sx={{ p: 2 }}>
          <ChromePicker
            color={tempColor}
            onChange={handleColorChange}
            disableAlpha={disableAlpha}
            presetColors={presetColors}
          />
          <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={handleClose}>
              Cancel
            </Button>
            <Button size="small" variant="contained" onClick={handleApply}>
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>

      {helperText && <FormHelperText>{helperText}</FormHelperText>}
      {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
    </FormControl>
  );
};